import React, { useState } from "react";

import { mdiMore, mdiPlayCircleOutline } from "@mdi/js";
import { Icon } from "@mdi/react";
import { useTranslation } from "react-i18next";

import AdsParamsModal from "./AdsParamsModal";
import Button from "../button/Button";
import MediaEPG from "../contentTabs/media/MediaEPG";
import PlayerModal from "../playerModal/PlayerModal";

import type { JSX } from "react";

import type { GetMediaResponse } from "../../api/MediaAPI.types";
import type { NewURL, NewURLSearchParams } from "../../types/url";

interface ContentExternalSourceProps {
  content: GetMediaResponse;
  onItemUpdate: (content: GetMediaResponse) => void;
}

export default function ExternalSourceSection({
  content,
  onItemUpdate,
}: ContentExternalSourceProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { external_stream: externalStream } = content;

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isParamsOpen, setIsParamsOpen] = useState(false);

  const getUrlType = (url: string): string => {
    const firstPartUrl = url.split(/#|\?/g)[0];
    const ext = firstPartUrl ? firstPartUrl.split(".").pop() : null;

    if (ext === "m3u" || ext === "m3u8") return "m3u8";
    return "mp4";
  };

  const handleUrlChange = (value: string): void => {
    onItemUpdate({
      ...content,
      external_stream: {
        ...content.external_stream,
        type: "stream",
        url: value,
        url_type: getUrlType(value),
        player_code: null,
      },
    });
  };

  const contentURL =
    content.external_stream?.url &&
    (URL as unknown as NewURL).canParse(content.external_stream?.url)
      ? new URL(content.external_stream.url)
      : null; // URL.parse not supported yet

  const searchParams =
    contentURL &&
    (new URLSearchParams(contentURL.search) as unknown as NewURLSearchParams);

  return (
    <div className="editor-form-section pb-[0.78125rem]">
      <div className="editor-form-external-url">
        <div className="editor-form-section-title">{t("source_url")}</div>
        <div className="editor-form-external-box">
          <div className="editor-form-input-box-h">
            <input
              onChange={(e): void => handleUrlChange(e.target.value)}
              type="text"
              value={externalStream?.url ?? ""}
            />
          </div>
          <div className="flex">
            <Button
              className="ml-0!"
              onClick={(): void => setIsPreviewOpen(true)}
              size="xs"
              type="button"
              isDisabled={
                !content.external_stream?.uuid || content.status === "archived"
              }
            >
              <Icon path={mdiPlayCircleOutline} size={0.7} />
              {t("showVideo")}
            </Button>
            <Button
              isDisabled={!searchParams?.size}
              onClick={(): void => setIsParamsOpen(true)}
              size="xs"
              type="button"
              variant="secondary"
            >
              <Icon path={mdiMore} size={0.7} />
              {t("showAdParams")}
            </Button>
          </div>
        </div>
      </div>
      <PlayerModal
        className="aspect-video"
        content={content}
        isOpen={isPreviewOpen}
        onClose={(): void => setIsPreviewOpen(false)}
      />

      {searchParams ? (
        <AdsParamsModal
          isOpen={isParamsOpen}
          onClose={(): void => setIsParamsOpen(false)}
          searchParams={searchParams}
        />
      ) : null}

      <MediaEPG item={content} onItemUpdate={onItemUpdate} />
    </div>
  );
}
